/**
 * Usage quota context provider.
 * Centralizes the /auth/me/usage fetch so the dashboard, lesson, profile,
 * and top-nav share a single source of truth (one network round-trip, live
 * updates after submit via refetch).
 */

import type { ReactNode } from 'react'
import React, { createContext, useContext, useMemo } from 'react'
import { useAuthUser } from './AuthContext'
import { useUsageQuota, type UseUsageQuotaReturn, type UsageQuota } from '@/hooks/useUsageQuota'

const UsageQuotaContext = createContext<UseUsageQuotaReturn | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components -- Justified: custom hook export
export const useUsageQuotaContext = (): UseUsageQuotaReturn => {
    const ctx = useContext(UsageQuotaContext)
    if (ctx === undefined) {
        throw new Error('useUsageQuotaContext must be used within a UsageQuotaProvider')
    }
    return ctx
}

// eslint-disable-next-line react-refresh/only-export-components -- Justified: derived helper export
export const isLlmLockedFor = (usage: UsageQuota | null): boolean =>
    Boolean(usage && !usage.isUnlimited && usage.monthlyUsagePercent >= 100)

interface UsageQuotaProviderProps {
    children: ReactNode
    /** When false, the provider skips network fetches (default: true). */
    enabled?: boolean
    /** Skip API fetch (for Storybook/tests). Uses initialUsage directly. */
    skipFetch?: boolean
    /** Initial usage state when skipFetch is true. Default: null (no usage data). */
    initialUsage?: UsageQuota | null
}

/**
 * Base provider — no auth dependency. App-level mounting should use
 * {@link AuthScopedUsageQuotaProvider} which derives `enabled` from auth state.
 */
export const UsageQuotaProvider: React.FC<UsageQuotaProviderProps> = ({
    children,
    enabled = true,
    skipFetch = false,
    initialUsage = null,
}) => {
    const live = useUsageQuota({ enabled: !skipFetch && enabled })
    const skipValue = useMemo<UseUsageQuotaReturn>(
        () => ({ usage: initialUsage, loading: false, error: '', refetch: () => {} }),
        [initialUsage]
    )
    const value = skipFetch ? skipValue : live
    return <UsageQuotaContext.Provider value={value}>{children}</UsageQuotaContext.Provider>
}

/**
 * Production wrapper: reads auth state and gates the fetch on user presence.
 * Mount this inside `AuthProvider` in App.tsx.
 */
export const AuthScopedUsageQuotaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const user = useAuthUser()
    return <UsageQuotaProvider enabled={!!user}>{children}</UsageQuotaProvider>
}
